import { z } from 'zod';
import * as R from 'remeda';
import { BullhornApiEntities } from './external/bullhornSchemas/index.js';
import { RenderChartArgs } from './lib/renderChartArgs.js';
import { GeneratedFileFormat, GENERATED_FILE_FORMAT_META } from './models/configSchemas.js';
import {
  USER_MEMORY_MAX_CONTENT_LENGTH,
  USER_MEMORY_MAX_ENTRIES,
  UserMemoryEntryView,
} from './models/userMemorySchemas.js';

//--------------------------------------------------------------------------------------------------------------------//
//region ENTITY CONFIGURATION

// Searchable entities and their API type.
export const SEARCHABLE_ENTITIES = [
  'Candidate',
  'Placement',
  'JobOrder',
  'ClientCorporation',
  'ClientContact',
  'CorporateUser',
  'Note',
  'JobSubmission',
  'UserMessage',
  'Sendout',
  'Lead',
  'Task',
  'Opportunity',
  'Tearsheet',
] as const;

export type SearchableEntityName = (typeof SEARCHABLE_ENTITIES)[number];

export const isSearchableEntity = R.isIncludedIn(SEARCHABLE_ENTITIES);

// Default field configurations for each entity - used when LLM doesn't specify fields.
// These are curated safe defaults, not an exhaustive list of valid fields.
export const entityFieldConfigs = {
  Candidate: {
    search:
      'id,name,email,mobile,phone,dateAdded,dateAvailable,status,owner(id,firstName,lastName),primarySkills,secondarySkills,categories',
    detail:
      'id,name,firstName,lastName,email,mobile,phone,dateAdded,dateAvailable,status,owner(id,firstName,lastName),primarySkills,secondarySkills,categories,address(city,state),employmentPreference,source',
  },
  Placement: {
    search:
      'id,candidate(id,name,email,mobile,phone,status),clientCorporation(id,name),clientContact(id,name,email,mobile),dateBegin,dateEnd,status,jobOrder(id,title),owner(id,firstName,lastName)',
    detail:
      'id,candidate(id,name,email,mobile,phone,status),clientCorporation(id,name),clientContact(id,name,email,mobile),dateBegin,dateEnd,status,jobOrder(id,title),owner(id,firstName,lastName),employmentType,payRate,billingProfile',
  },
  JobOrder: {
    search:
      'id,title,clientCorporation(id,name),clientContact(id,name,email,mobile),dateAdded,dateEnd,startDate,status,owner(id,firstName,lastName),assignedUsers(id,firstName,lastName),employmentType',
    detail:
      'id,title,clientCorporation(id,name),clientContact(id,name,email,mobile),dateAdded,dateEnd,startDate,status,owner(id,firstName,lastName),assignedUsers(id,firstName,lastName),employmentType,numOpenings,publicDescription',
  },
  ClientCorporation: {
    search: 'id,name,status,address(city,state),phone,owners(id,firstName,lastName)',
    detail:
      'id,name,status,address(city,state,address1),phone,billingPhone,fax,owners(id,firstName,lastName),dateAdded',
  },
  ClientContact: {
    search:
      'id,name,email,mobile,phone,clientCorporation(id,name),status,owner(id,firstName,lastName)',
    detail:
      'id,name,firstName,lastName,email,mobile,phone,clientCorporation(id,name),status,owner(id,firstName,lastName),dateAdded,occupation',
  },
  CorporateUser: {
    search: 'id,name,firstName,lastName,email',
    detail: 'id,name,firstName,lastName,email,mobile,phone',
  },
  Note: {
    search:
      'id,dateAdded,commentingPerson(id,firstName,lastName),action,comments,candidates,jobOrders,placements(id,candidate,jobOrder)',
    detail:
      'id,dateAdded,commentingPerson(id,firstName,lastName),action,comments,clientContacts,candidates,jobOrders,placements(id,candidate,jobOrder),primaryDepartmentName',
  },
  JobSubmission: {
    search:
      'id,candidate(id,name,email,mobile,phone,status),jobOrder(id,title,clientCorporation),status,dateAdded,dateLastModified,source,sendingUser(id,firstName,lastName)',
    detail:
      'id,candidate(id,name,email,mobile,phone,status),jobOrder(id,title,clientCorporation),status,dateAdded,dateLastModified,source,sendingUser(id,firstName,lastName)',
  },
  UserMessage: {
    search:
      'id,dateAdded,subject,externalFrom,externalTo,sender(id,name,email),toRecipients(id,name,email),smtpReceiveDate,isPrivate',
    detail:
      'id,dateAdded,comments,subject,externalFrom,externalTo,externalCC,sender(id,name,email),toRecipients(id,name,email),ccRecipients(id,name,email),smtpSendDate,smtpReceiveDate,threadID,isPrivate,messageFiles(id,name),emailRecipients,clientCorporations(id,name)',
  },
  Sendout: {
    // Note: isRead omitted - it's an unreliable approximate value that confuses the LLM
    search:
      'id,candidate(id,name,email,mobile,phone),clientCorporation(id,name),clientContact(id,name,email),jobOrder(id,title),dateAdded,user(id,firstName,lastName)',
    detail:
      'id,candidate(id,name,email,mobile,phone),clientCorporation(id,name),clientContact(id,name,email),jobOrder(id,title),dateAdded,user(id,firstName,lastName)',
  },
  Lead: {
    search:
      'id,name,firstName,lastName,email,phone,mobile,occupation,companyName,clientCorporation(id,name),status,leadSource,owner(id,firstName,lastName),dateAdded',
    detail:
      'id,name,firstName,lastName,email,phone,mobile,occupation,companyName,clientCorporation(id,name),status,leadSource,owner(id,firstName,lastName),dateAdded,address(city,state),description,source,dateLastModified',
  },
  Task: {
    search:
      'id,subject,isCompleted,dateBegin,dateEnd,type,priority,owner(id,firstName,lastName),candidate(id,name),clientContact(id,name),dateAdded',
    detail:
      'id,subject,isCompleted,dateBegin,dateEnd,type,priority,owner(id,firstName,lastName),candidate(id,name),clientContact(id,name),dateAdded,description,dateCompleted,jobOrder(id,title),opportunity(id,title),lead(id,name),isPrivate',
  },
  Opportunity: {
    search:
      'id,title,clientCorporation(id,name),clientContact(id,name,email),status,isOpen,owner(id,firstName,lastName),dateAdded,expectedCloseDate,numOpenings',
    detail:
      'id,title,clientCorporation(id,name),clientContact(id,name,email),status,isOpen,owner(id,firstName,lastName),dateAdded,expectedCloseDate,numOpenings,description,dealValue,winProbabilityPercent,reasonClosed,dateLastModified',
  },
  Tearsheet: {
    search: 'id,name,description,isPrivate,owner(id,firstName,lastName),dateAdded,dateLastModified',
    detail:
      'id,name,description,isPrivate,owner(id,firstName,lastName),dateAdded,dateLastModified,candidates(id,name),clientContacts(id,name),jobOrders(id,title)',
  },
} as const satisfies Record<SearchableEntityName, { search: string; detail: string }>;

// Helper to get default fields for an entity with proper type narrowing
export function getDefaultFields(entity: string, mode: 'search' | 'detail'): string {
  if (!isSearchableEntity(entity)) {
    return 'id';
  }
  return entityFieldConfigs[entity][mode];
}

//endregion
//--------------------------------------------------------------------------------------------------------------------//
//region ZOD SCHEMAS FOR TOOL ARGUMENT VALIDATION

export const BaseArgs = z.object({
  displayDescription: z.string().describe('Short user-friendly description'),
  fields: z
    .string()
    .optional()
    .describe(
      'Comma-separated fields to return. Leave empty for smart defaults, which are usually best. Sub-fields use parentheses, e.g. address(city,state).'
    ),
  limit: z
    .number()
    .min(1)
    .max(50)
    .optional()
    .default(10)
    .describe('Maximum results to return (default: 10, max: 50)'),
  start: z.number().min(0).optional().describe('Result offset for pagination (default: 0)'),
  sort: z
    .string()
    .nullish()
    .describe(
      'Sort field and direction (e.g., "dateAdded", "-dateAdded" for descending). Prefix with - for descending order.'
    ),
  fieldFilters: z
    .array(
      // Strict, so an older prompt's `operator` key fails the call instead of silently becoming an OR
      z.strictObject({
        field: z
          .string()
          .describe('Field name from lookupFields, e.g. customText11, owner, address.city'),
        values: z
          .array(z.union([z.string(), z.number(), z.boolean()]))
          .nullish()
          .describe('Values to match. Required for any and all, ignored for set and notSet'),
        match: z
          .enum(['any', 'all', 'set', 'notSet'])
          .nullish()
          .describe(
            'any (default): the field holds at least one of the values. all: it holds every value (multi-value fields on candidates, jobs, notes, companies, contacts and emails). set: it has a value. notSet: it is blank. Use all when the user lists several values a multi-value field should hold together (licensed in TX and CA); use any only when they said either/or.'
          ),
      })
    )
    .nullish()
    .describe(
      'Filter on any entity field, including custom fields. Get names and option values from lookupFields. Every entry must match (AND). Candidates, jobs, notes, companies, contacts, leads, opportunities and emails match values as phrases; placements, sendouts, submissions, tasks and tearsheets match the whole value. Use the date params for dates, and the address, name, title and subject params for those fields, which match more broadly there; set and notSet work on any field.'
    ),
});
export type BaseArgs = z.infer<typeof BaseArgs>;

/** Extra query params for Bullhorn Search API (e.g. entityId for UserMessage scoping) */
export type SearchExtraParams = { entityId?: number };

// Date filter description helpers
const dateAddedAfterDesc = 'YYYY-MM-DD - find records added on or after this date';
const dateAddedBeforeDesc = 'YYYY-MM-DD - find records added on or before this date';
const dateAfterDesc = (field: string) =>
  `YYYY-MM-DD - find records whose ${field} is on or after this date`;
const dateBeforeDesc = (field: string) =>
  `YYYY-MM-DD - find records whose ${field} is on or before this date`;

// Search API tools (Lucene queries) - extend base schema
// Use .nullish() because LLMs often send explicit null values
export const SearchNotesArgs = BaseArgs.safeExtend({
  candidateIds: z
    .array(z.number())
    .nullish()
    .describe('Candidate IDs - finds notes associated with these candidates'),
  contactIds: z
    .array(z.number())
    .nullish()
    .describe('Client contact IDs - finds notes associated with these contacts'),
  recruiterIds: z
    .array(z.number())
    .nullish()
    .describe(
      'CorporateUser IDs of the note AUTHORS. This is the only correct way to answer "notes I/<recruiter> wrote" - never enumerate their candidates instead.'
    ),
  companyId: z
    .number()
    .nullish()
    .describe(
      'Company (ClientCorporation) ID - finds notes associated with contacts at this company'
    ),
  action: z.string().nullish().describe('Note action type (e.g., "Left Message", "Outbound SMS")'),
  comments: z
    .string()
    .nullish()
    .describe('Search note content using Lucene syntax (e.g., "RPSGT OR PSG", "workers comp")'),
  dateAddedAfter: z.iso.date().nullish().describe(dateAddedAfterDesc),
  dateAddedBefore: z.iso.date().nullish().describe(dateAddedBeforeDesc),
});
export type SearchNotesArgs = z.infer<typeof SearchNotesArgs>;

export const AddressFilter = z
  .object({
    city: z.array(z.string()).optional().describe('City names'),
    state: z
      .array(z.string())
      .optional()
      .describe(
        'State/province as full name or abbreviation (e.g., ["Arizona", "ON"]). Try the full name first; if a search returns no results, retry with the abbreviation.'
      ),
    zip: z.array(z.string()).optional().describe('ZIP codes'),
  })
  .nullish()
  .describe('Filter by location (arrays allow OR logic)');
export type AddressFilter = z.infer<typeof AddressFilter>;

export const SearchCandidatesArgs = BaseArgs.safeExtend({
  ids: z
    .array(z.number())
    .nullish()
    .describe(
      'Candidate IDs — fetch a specific set of candidates in one call (e.g. to verify searchCandidateProfiles results)'
    ),
  name: z.string().nullish().describe('Candidate name to search for'),
  status: z.string().nullish().describe('Candidate status (e.g., "Active", "Placed")'),
  ownerIds: z.array(z.number()).nullish().describe('Recruiter/owner IDs'),
  skillIds: z.array(z.number()).nullish().describe('Primary skill IDs'),
  address: AddressFilter,
  dateAddedAfter: z.iso.date().nullish().describe(dateAddedAfterDesc),
  dateAddedBefore: z.iso.date().nullish().describe(dateAddedBeforeDesc),
});
export type SearchCandidatesArgs = z.infer<typeof SearchCandidatesArgs>;

export const SearchJobOrdersArgs = BaseArgs.safeExtend({
  ids: z
    .array(z.number())
    .nullish()
    .describe(
      'JobOrder IDs — fetch a specific set of jobs in one call (e.g. to verify searchJobProfiles results)'
    ),
  clientCorporationId: z.number().nullish().describe('Company ID to filter by'),
  status: z
    .array(z.string())
    .nullish()
    .describe(
      'Job order statuses to include (OR logic). Omit this to skip the statuses this tenant marked finished — that is the right choice for "open jobs" or "jobs we are working". Set it to search exact statuses, including closed ones, e.g. ["Filled", "Closed"].'
    ),
  ownerIds: z
    .array(z.number())
    .nullish()
    .describe(
      'CorporateUser IDs of the job order OWNER - commonly the account manager who owns the client relationship. On some tenants it is also a default or pool user, so it does not always mean someone actively working the job.'
    ),
  assignedUserIds: z
    .array(z.number())
    .nullish()
    .describe(
      'CorporateUser IDs of the users ASSIGNED to the job order - commonly the recruiters sourcing for it. Independent of ownerIds; both can be set.'
    ),
  dateAddedAfter: z.iso.date().nullish().describe(dateAddedAfterDesc),
  dateAddedBefore: z.iso.date().nullish().describe(dateAddedBeforeDesc),
  startDateAfter: z.iso.date().nullish().describe(dateAfterDesc('startDate (when the job starts)')),
  startDateBefore: z.iso
    .date()
    .nullish()
    .describe(dateBeforeDesc('startDate (when the job starts)')),
  dateEndAfter: z.iso.date().nullish().describe(dateAfterDesc('dateEnd (when the job ends)')),
  dateEndBefore: z.iso.date().nullish().describe(dateBeforeDesc('dateEnd (when the job ends)')),
  title: z
    .array(z.string())
    .nullish()
    .describe(
      'Job title keywords (OR logic). Single words get prefix matching (e.g., "RN" matches "RN - ICU"). Multi-word phrases require exact match (e.g., "Sleep Tech"). For broader matching, use single keywords like ["Sleep", "Polysomnogram", "PSG"].'
    ),
  address: AddressFilter,
});
export type SearchJobOrdersArgs = z.infer<typeof SearchJobOrdersArgs>;

export const SearchCompaniesArgs = BaseArgs.safeExtend({
  name: z.string().nullish().describe('Company name to search for'),
  status: z.string().nullish().describe('Company status'),
  state: z
    .string()
    .nullish()
    .describe(
      'State/province as full name or abbreviation (e.g., "Ohio" or "ON"). Try the full name first; if a search returns no results, retry with the abbreviation.'
    ),
});
export type SearchCompaniesArgs = z.infer<typeof SearchCompaniesArgs>;

export const SearchContactsArgs = BaseArgs.safeExtend({
  clientCorporationId: z.number().nullish().describe('Company ID to filter by'),
  name: z.string().nullish().describe('Contact name to search for'),
  status: z.string().nullish().describe('Contact status'),
});
export type SearchContactsArgs = z.infer<typeof SearchContactsArgs>;

export const SearchLeadsArgs = BaseArgs.safeExtend({
  clientCorporationId: z.number().nullish().describe('Company ID to filter by'),
  name: z.string().nullish().describe('Lead name to search for'),
  status: z.string().nullish().describe('Lead status (e.g., "New Lead")'),
  ownerIds: z.array(z.number()).nullish().describe('Owner (recruiter) IDs'),
  dateAddedAfter: z.iso.date().nullish().describe(dateAddedAfterDesc),
  dateAddedBefore: z.iso.date().nullish().describe(dateAddedBeforeDesc),
});
export type SearchLeadsArgs = z.infer<typeof SearchLeadsArgs>;

export const SearchOpportunitiesArgs = BaseArgs.safeExtend({
  clientCorporationId: z.number().nullish().describe('Company ID to filter by'),
  title: z.string().nullish().describe('Opportunity title to search for'),
  status: z.string().nullish().describe('Opportunity status (e.g., "Negotiating")'),
  ownerIds: z.array(z.number()).nullish().describe('Owner (account manager) IDs'),
  isOpen: z
    .boolean()
    .nullish()
    .describe('true for the active pipeline, false for closed ones; omit for both'),
  dateAddedAfter: z.iso.date().nullish().describe(dateAddedAfterDesc),
  dateAddedBefore: z.iso.date().nullish().describe(dateAddedBeforeDesc),
  expectedCloseDateAfter: z.iso.date().nullish().describe(dateAfterDesc('expectedCloseDate')),
  expectedCloseDateBefore: z.iso.date().nullish().describe(dateBeforeDesc('expectedCloseDate')),
});
export type SearchOpportunitiesArgs = z.infer<typeof SearchOpportunitiesArgs>;

// Query API tools (SQL WHERE) - extend base schema
// Use .nullish() because LLMs often send explicit null values
export const SearchPlacementsArgs = BaseArgs.safeExtend({
  candidateId: z.number().nullish().describe('Candidate ID'),
  clientCorporationId: z.number().nullish().describe('Company ID'),
  jobOrderId: z.number().nullish().describe('Job order ID'),
  status: z.string().nullish().describe('Placement status (e.g., "Approved", "Completed")'),
  dateBeginAfter: z.iso.date().nullish().describe(dateAfterDesc('dateBegin (assignment start)')),
  dateBeginBefore: z.iso.date().nullish().describe(dateBeforeDesc('dateBegin (assignment start)')),
  dateEndAfter: z.iso.date().nullish().describe(dateAfterDesc('dateEnd (assignment end)')),
  dateEndBefore: z.iso
    .date()
    .nullish()
    .describe(
      `${dateBeforeDesc('dateEnd (assignment end)')} - pair with dateEndAfter for "placements ending in the next N days"`
    ),
  dateAddedAfter: z.iso
    .date()
    .nullish()
    .describe(dateAfterDesc('dateAdded (when the placement record was created)')),
  dateAddedBefore: z.iso
    .date()
    .nullish()
    .describe(dateBeforeDesc('dateAdded (when the placement record was created)')),
  candidateState: z.string().nullish().describe('Filter by candidate state (e.g., "OH")'),
});
export type SearchPlacementsArgs = z.infer<typeof SearchPlacementsArgs>;

export const SearchSendoutsArgs = BaseArgs.safeExtend({
  candidateId: z.number().nullish().describe('Candidate ID'),
  clientCorporationId: z.number().nullish().describe('Company ID'),
  candidateState: z.string().nullish().describe('Filter by candidate state (e.g., "OH")'),
  dateAddedAfter: z.iso.date().nullish().describe(dateAddedAfterDesc),
  dateAddedBefore: z.iso.date().nullish().describe(dateAddedBeforeDesc),
});
export type SearchSendoutsArgs = z.infer<typeof SearchSendoutsArgs>;

export const SearchJobSubmissionsArgs = BaseArgs.safeExtend({
  candidateId: z.number().nullish().describe('Candidate ID'),
  clientCorporationId: z
    .number()
    .nullish()
    .describe("Company ID, matched through the submission's job order company"),
  jobOrderId: z.number().nullish().describe('Job order ID'),
  status: z.string().nullish().describe('Submission status'),
  dateAddedAfter: z.iso.date().nullish().describe(dateAddedAfterDesc),
  dateAddedBefore: z.iso.date().nullish().describe(dateAddedBeforeDesc),
  dateLastModifiedAfter: z.iso.date().nullish().describe(dateAfterDesc('dateLastModified')),
  dateLastModifiedBefore: z.iso.date().nullish().describe(dateBeforeDesc('dateLastModified')),
});
export type SearchJobSubmissionsArgs = z.infer<typeof SearchJobSubmissionsArgs>;

export const SearchTasksArgs = BaseArgs.safeExtend({
  candidateId: z.number().nullish().describe('Candidate ID'),
  clientContactId: z.number().nullish().describe('Contact ID'),
  jobOrderId: z.number().nullish().describe('Job order ID'),
  ownerId: z.number().nullish().describe('Owner (assignee) user ID'),
  isCompleted: z
    .boolean()
    .nullish()
    .describe('true for completed tasks, false for open ones; omit for both'),
  taskType: z.string().nullish().describe('Task type (e.g., "Call", "Email", "To Do")'),
  dateBeginAfter: z.iso.date().nullish().describe(dateAfterDesc('dateBegin (task start)')),
  dateBeginBefore: z.iso.date().nullish().describe(dateBeforeDesc('dateBegin (task start)')),
});
export type SearchTasksArgs = z.infer<typeof SearchTasksArgs>;

export const SearchTearsheetsArgs = BaseArgs.safeExtend({
  ownerId: z.number().nullish().describe('Owner user ID'),
  isPrivate: z
    .boolean()
    .nullish()
    .describe('true for private tearsheets, false for shared ones; omit for both'),
  name: z.string().nullish().describe('Exact tearsheet name'),
  dateAddedAfter: z.iso.date().nullish().describe(dateAddedAfterDesc),
  dateAddedBefore: z.iso.date().nullish().describe(dateAddedBeforeDesc),
});
export type SearchTearsheetsArgs = z.infer<typeof SearchTearsheetsArgs>;

// A write tool, not a search: ATS-neutral params only, the ATS payload is assembled server-side.
export const SubmitCandidateToShortlistArgs = z.object({
  displayDescription: z.string().describe('Short user-friendly description'),
  candidateId: z.number().int().positive().describe('ID of the candidate to shortlist'),
  jobOrderId: z.number().int().positive().describe('ID of the job to shortlist the candidate for'),
  comments: z
    .string()
    .max(2000)
    .optional()
    .describe(
      'Note saved on the submission. Only include it when the user gave you the note text or a reason to record; otherwise omit it'
    ),
});
export type SubmitCandidateToShortlistArgs = z.infer<typeof SubmitCandidateToShortlistArgs>;

export const SubmitCandidateToShortlistResult = z.object({
  result: z.enum(['created', 'already_exists']),
  candidate: z.object({ id: z.number(), name: z.string().optional() }),
  job: z.object({
    id: z.number(),
    title: z.string().optional(),
    client: z.string().optional(),
    status: z.string().optional(),
  }),
  submission: z.object({
    id: z.number(),
    status: z.string().optional(),
    addedBy: z.string().optional(),
    dateAdded: z.string().optional(),
  }),
});
export type SubmitCandidateToShortlistResult = z.infer<typeof SubmitCandidateToShortlistResult>;

export const EmailEntityType = z.enum([
  'Candidate',
  'ClientContact',
  'CorporateUser',
  'ClientCorporation',
]);
export type EmailEntityType = z.infer<typeof EmailEntityType>;

export const SearchEmailsArgs = BaseArgs.safeExtend({
  entityId: z
    .number()
    .describe(
      'The ID of the entity to find emails for. Works with Candidates, ClientContacts, CorporateUsers (recruiters), and ClientCorporations (companies).'
    ),
  entityType: EmailEntityType.describe(
    'The type of entity the entityId belongs to. Determines how the email search is scoped.'
  ),
  subject: z.string().nullish().describe('Search email subject using Lucene syntax'),
  dateAddedAfter: z.iso.date().nullish().describe(dateAddedAfterDesc),
  dateAddedBefore: z.iso.date().nullish().describe(dateAddedBeforeDesc),
});
export type SearchEmailsArgs = z.infer<typeof SearchEmailsArgs>;

// Utility tools - displayDescription required to match tool definitions
export const LookupFieldsArgs = z.object({
  displayDescription: z.string().describe('Short user-friendly description'),
  entityType: z.enum(SEARCHABLE_ENTITIES).describe('The entity type to get field information for'),
  fields: z
    .array(z.string())
    .nullish()
    .describe(
      'Only these field names, each with its full picklist. Omit to list every field with its first 10 values.'
    ),
});
export type LookupFieldsArgs = z.infer<typeof LookupFieldsArgs>;

export const GetEntityArgs = z.object({
  displayDescription: z.string().describe('Short user-friendly description'),
  entity: z.enum(SEARCHABLE_ENTITIES).describe('The entity type to fetch'),
  id: z.number().describe('The Bullhorn ID of the entity'),
  fields: z
    .string()
    .optional()
    .describe(
      'Comma-separated fields. Omit to use curated defaults. Use lookupFields first if you need specific fields.'
    ),
});
export type GetEntityArgs = z.infer<typeof GetEntityArgs>;

export const ResolveCompanyArgs = z.object({
  displayDescription: z.string().describe('Short user-friendly description'),
  companyName: z.string().describe('Company name to search for'),
});
export type ResolveCompanyArgs = z.infer<typeof ResolveCompanyArgs>;

export const ResolveRecruiterArgs = z.object({
  displayDescription: z.string().describe('Short user-friendly description'),
  firstName: z.string().optional().describe("Recruiter's first name"),
  lastName: z.string().optional().describe("Recruiter's last name"),
  email: z.string().optional().describe("Recruiter's email address"),
});
export type ResolveRecruiterArgs = z.infer<typeof ResolveRecruiterArgs>;

export const GenerateFileArgs = z.object({
  displayDescription: z.string().describe('Short user-friendly description'),
  filename: z.string().describe('Base filename without an extension.'),
  content: z
    .string()
    .describe(
      'File content. For .docx, write Markdown (# headings, **bold**, *italic*, - bullets) — it is rendered to a formatted Word document. For .csv/.txt, plain text.'
    ),
  format: GeneratedFileFormat.describe('Output file format.'),
});
export type GenerateFileArgs = z.infer<typeof GenerateFileArgs>;

export const RenderChartToolArgs = z.object({
  displayDescription: z.string().describe('Short user-friendly description'),
  chart: RenderChartArgs,
});
export type RenderChartToolArgs = z.infer<typeof RenderChartToolArgs>;

const IdealCandidateQuery = z.object({
  queryBy: z.literal('idealProfile'),
  licenseType: z
    .string()
    .nullish()
    .describe('License/credential type, e.g. "RN", "Physical Therapist", "Cath Lab Tech"'),
  specialty: z
    .string()
    .nullish()
    .describe('Clinical specialty, e.g. "ICU", "Med Surg", "Cardiology"'),
  discipline: z
    .string()
    .nullish()
    .describe(
      'Professional functional area, e.g. "Software Development", "Instrumentation & Controls Engineering"'
    ),
  industry: z
    .string()
    .nullish()
    .describe('Professional industry/domain, e.g. "Oil & Gas", "IT", "Finance"'),
  roleType: z.enum(['Individual Contributor', 'People Manager']).nullish(),
  skills: z.string().array().nullish().describe('Specific skills the ideal candidate should have'),
  certifications: z
    .string()
    .array()
    .nullish()
    .describe('Certifications the ideal candidate should hold'),
  additionalContext: z
    .string()
    .max(500)
    .nullish()
    .describe('Extra free-text requirements that do not fit the structured fields'),
});
export type IdealCandidateQuery = z.infer<typeof IdealCandidateQuery>;

const CandidatesLikeCandidateQuery = z.object({
  queryBy: z.literal('similarCandidate'),
  similarToCandidateId: z
    .number()
    .describe(
      'Find candidates similar to this Bullhorn Candidate ID using their stored profile; statesLicensed and excludeCandidateIds still apply'
    ),
});

const CandidatesForJobQuery = z.object({
  queryBy: z.literal('similarJob'),
  similarToJobId: z
    .number()
    .describe(
      "Find candidates matching this Bullhorn JobOrder ID using its stored job profile. The job's own required state licenses are applied as the statesLicensed filter automatically — pass statesLicensed to override. PREFERRED over manually distilling the job description."
    ),
});

export const SearchCandidateProfilesArgs = z.object({
  displayDescription: z.string().describe('Short user-friendly description'),
  profileQuery: z
    .discriminatedUnion('queryBy', [
      IdealCandidateQuery,
      CandidatesLikeCandidateQuery,
      CandidatesForJobQuery,
    ])
    .describe(
      'What to search with: an ideal-candidate description (idealProfile), an existing candidate whose stored profile becomes the query (similarCandidate), or a job whose stored profile becomes the query (similarJob)'
    ),
  excludeCandidateIds: z
    .array(z.number())
    .nullish()
    .describe(
      'Bullhorn Candidate IDs to exclude (e.g. the current candidate or already-shown results)'
    ),
  statesLicensed: z
    .array(z.string())
    .nullish()
    .describe(
      'Filter by 2-letter state license codes (OR logic; Compact licenses match automatically). Strict — candidates with no recorded licensure are excluded. When a similarJob search auto-derives this filter and finds no matches, it retries unfiltered and the result includes a note.'
    ),
  limit: z
    .number()
    .min(1)
    .max(25)
    .optional()
    .default(10)
    .describe('Maximum results (default 10, max 25)'),
});
export type SearchCandidateProfilesArgs = z.infer<typeof SearchCandidateProfilesArgs>;

const JobsForCandidateQuery = z.object({
  queryBy: z.literal('similarCandidate'),
  similarToCandidateId: z
    .number()
    .describe(
      'Find open jobs matching this Bullhorn Candidate ID using their stored profile. Job locations are filtered to the candidate\'s licensed + preferred states automatically — pass states to override. PREFERRED for "find jobs for this candidate".'
    ),
});

const JobsLikeJobQuery = z.object({
  queryBy: z.literal('similarJob'),
  similarToJobId: z
    .number()
    .describe(
      'Find open jobs similar to this Bullhorn JobOrder ID using its stored profile; states and excludeJobIds still apply'
    ),
});

const IdealJobQuery = z.object({
  queryBy: z.literal('idealProfile'),
  licenseType: z
    .string()
    .nullish()
    .describe('License/credential type the role requires, e.g. "RN", "Physical Therapist"'),
  specialty: z
    .string()
    .nullish()
    .describe('Clinical specialty of the role, e.g. "ICU", "Med Surg", "Cardiology"'),
  discipline: z
    .string()
    .nullish()
    .describe(
      'Professional functional area, e.g. "Software Development", "Instrumentation & Controls Engineering"'
    ),
  industry: z
    .string()
    .nullish()
    .describe('Professional industry/domain, e.g. "Oil & Gas", "IT", "Finance"'),
  roleType: z.enum(['Individual Contributor', 'People Manager']).nullish(),
  skills: z.string().array().nullish().describe('Skills the role should call for'),
  certifications: z.string().array().nullish().describe('Certifications the role should require'),
  additionalContext: z
    .string()
    .max(500)
    .nullish()
    .describe('Extra free-text requirements that do not fit the structured fields'),
});

export const SearchJobProfilesArgs = z.object({
  displayDescription: z.string().describe('Short user-friendly description'),
  profileQuery: z
    .discriminatedUnion('queryBy', [IdealJobQuery, JobsLikeJobQuery, JobsForCandidateQuery])
    .describe(
      'What to search with: an ideal-job description (idealProfile), a candidate whose stored profile becomes the query (similarCandidate), or a job whose stored profile becomes the query (similarJob)'
    ),
  excludeJobIds: z
    .array(z.number())
    .nullish()
    .describe('Bullhorn JobOrder IDs to exclude (e.g. already-shown results)'),
  states: z
    .array(z.string())
    .nullish()
    .describe(
      'Filter to jobs located in these states (2-letter codes; "Compact" means any ENLC state). Remote jobs match any state. Overrides the automatic candidate-state filter of similarCandidate. When a similarCandidate search auto-derives this filter and finds no matches, it retries unfiltered and the result includes a note.'
    ),
  limit: z
    .number()
    .min(1)
    .max(25)
    .optional()
    .default(10)
    .describe('Maximum results (default 10, max 25)'),
});
export type SearchJobProfilesArgs = z.infer<typeof SearchJobProfilesArgs>;

export function makeGenerateFileArgs(
  formats: readonly GeneratedFileFormat[],
  maxEntities?: number
) {
  // Callers omit the tool when no formats are allowed; z.enum([]) throws and the cast below would lie.
  if (formats.length === 0) throw new Error('makeGenerateFileArgs requires at least one format');
  formats = R.unique(formats);

  const hasDocx = formats.includes('docx');
  const plainExts = formats
    .filter((f) => f !== 'docx')
    .map((f) => GENERATED_FILE_FORMAT_META[f].ext);
  // Soft, all-format guidance. The hard CSV row backstop (maxCsvRows) is separate and hidden here.
  const entityNote =
    maxEntities ?
      ` Cap: ${maxEntities} primary entities per chat — count one per primary entity (candidate, placement, company), not per child record; a candidate's notes, emails, and related records all belong to that one entity, so all of one candidate's notes count as 1, not one per note. This is a running total across the whole chat, including entities the user adds one at a time in later messages. Once the chat's total reaches ${maxEntities}, do not add any more — not in a new export, an addendum, an appended file, or a "separate" one — tell the user the chat is at its ${maxEntities}-entity limit and must start a new chat for the rest.`
    : '';
  const contentDesc =
    'File content.' +
    (hasDocx ?
      ' For .docx, write Markdown (# headings, **bold**, *italic*, - bullets, | tables |) — rendered to a formatted Word document.'
    : '') +
    (plainExts.length ? ` For ${plainExts.join('/')}, plain text.` : '') +
    entityNote;
  return GenerateFileArgs.safeExtend({
    format: GeneratedFileFormat.extract(formats).describe(
      `Output file format. ${hasDocx ? ' PDF is not supported — use .docx instead.' : ''}`
    ),
    content: z.string().describe(contentDesc),
  });
}

// Outer generateFile description, also derived from `formats` (never the static all-formats string).
export function generateFileDescription(formats: readonly GeneratedFileFormat[]): string {
  const exts = formats.map((f) => GENERATED_FILE_FORMAT_META[f].ext).join(', ');
  return `Generate a downloadable file. Supported formats: ${exts}.${formats.includes('docx') ? ' PDF is not supported; use .docx for document requests.' : ''}`;
}

// Flat, not a discriminated union: a union serializes to a root `oneOf` and OpenAI needs a root object, so the check carries the per-operation requirements.
export const UpdateMemoryArgs = z
  .object({
    displayDescription: z.string().describe('Short user-friendly description'),
    operation: z.enum(['add', 'update', 'delete']),
    // TODO(#2436): tighten to ObjectIdString
    id: z
      .string()
      .optional()
      .describe('The [id] of an existing memory entry. Required for update and delete.'),
    content: z
      .string()
      .max(USER_MEMORY_MAX_CONTENT_LENGTH)
      .optional()
      .describe(
        `The fact to remember, as one concise sentence of at most ${USER_MEMORY_MAX_CONTENT_LENGTH} characters. Required for add and update.`
      ),
  })
  .check((ctx) => {
    const { operation, id, content } = ctx.value;
    if (operation !== 'add' && !id) {
      ctx.issues.push({
        code: 'custom',
        input: id,
        path: ['id'],
        message: `id is required for ${operation}.`,
      });
    }
    if (operation !== 'delete' && !content) {
      ctx.issues.push({
        code: 'custom',
        input: content,
        path: ['content'],
        message: `content is required for ${operation}.`,
      });
    }
  });
export type UpdateMemoryArgs = z.infer<typeof UpdateMemoryArgs>;

export const UpdateMemoryResult = z.object({
  entries: UserMemoryEntryView.array(),
});
export type UpdateMemoryResult = z.infer<typeof UpdateMemoryResult>;

//endregion
//--------------------------------------------------------------------------------------------------------------------//
//region TOOL DEFINITIONS

export const ToolName = z.enum([
  'searchNotes',
  'searchCandidates',
  'searchJobOrders',
  'searchCompanies',
  'searchContacts',
  'searchPlacements',
  'searchSendouts',
  'searchJobSubmissions',
  'searchLeads',
  'searchTasks',
  'searchOpportunities',
  'searchTearsheets',
  'searchEmails',
  'searchCandidateProfiles',
  'searchJobProfiles',
  'getEntity',
  'resolveCompany',
  'resolveRecruiter',
  'generateFile',
  'lookupFields',
  'renderChart',
  'updateMemory',
  'submitCandidateToShortlist',
]);
export type ToolName = z.infer<typeof ToolName>;

export const SearchResult = z.object({
  displayDescription: z.string(),
  results: z.unknown(),
  total: z.number(),
  showing: z.number(),
  hasMore: z.boolean().optional(),
  query: z.string(),
  note: z.string().optional(),
});
export type SearchResult = z.infer<typeof SearchResult>;

/**
 * Simplified field info for LLM consumption.
 * Intentionally a subset of BullhornApiMeta.Field to reduce token usage.
 */
export const FieldInfo = z.object({
  name: z.string(),
  label: z.string(),
  type: z.string(),
  formatNotes: z.string().optional(),
  options: z.string().array().optional(),
});
export type FieldInfo = z.infer<typeof FieldInfo>;

export const FileResult = z.object({
  filename: z.string(),
  fileId: z.string(),
  signedUrl: z.string(),
  mimeType: z.string(),
  size: z.number(),
  markdown: z.string().optional(),
});
export type FileResult = z.infer<typeof FileResult>;

export interface ToolDefinition<
  In extends z.ZodObject = z.ZodObject,
  Out extends z.ZodType = z.ZodType,
> {
  input: In;
  output: Out;
  description: string;
  // ATS-write tools pause for explicit user confirmation unless the user holds an "always allow" grant
  confirmation?: {
    required: true;
    // In-chat permission box header
    prompt: string;
    // User options page label for the permission grant
    label: string;
  };
}

const PROFILE_SEARCH_RANK_GUIDANCE = `Results are ordered best match first. Each result's rank is its position within this one search and is not comparable across searches, so merge results from several searches by rank. Rank is a semantic-similarity order between each profile and the query, not a qualification or fit score: never present it to the user as a score or percentage, and if asked why a result ranked where it did, explain that the order comes from semantic similarity between its profile and the query.`;
const QUERY_API_PAGING_GUIDANCE = `"total" is this page's row count, not a match count. Page with "start" while "hasMore" is true.`;

export const TOOL_DEFINITIONS = {
  searchNotes: {
    input: SearchNotesArgs,
    output: SearchResult,
    description: `Search for Notes in Bullhorn.

      Never use notes to answer a question about emails. Notes with action Email are outbound copies only (sequences, mass mails) and miss every reply a candidate or contact sent, so a note search reports inbound email as absent. Use searchEmails for any email question.

      To find notes WRITTEN BY a recruiter, pass their CorporateUser id in recruiterIds - it filters on the author. Do not page unfiltered notes or enumerate the recruiter's candidates; recruiters write notes on candidates they do not own.

      Available return fields: id, dateAdded, action, comments, commentingPerson(id,firstName,lastName), candidates, clientContacts, jobOrders, placements, personReference.
      Do NOT use as return fields: candidate, recruiter, description, person, addUser - these fields do not exist.

      isDeleted:false is automatically added.

      A note's "comments" may contain "MMS URL(s):" followed by image links. To display one, output the exact image URL verbatim (or standard markdown ![](URL)) in your reply — it is parsed and rendered inline automatically.`,
  },
  searchCandidates: {
    input: SearchCandidatesArgs,
    output: SearchResult,
    description: `Search for Candidates in Bullhorn. isDeleted:false is automatically added.

      To find candidates MATCHING a job, role, or another candidate, prefer searchCandidateProfiles (semantic match) — use this tool for lookups by name/status/owner/metadata. To verify searchCandidateProfiles results, pass all shortlisted ids in ONE call via the ids parameter.
      There is no free-text filter on this tool — for "candidates with X experience/certification" use searchCandidateProfiles, which matches by meaning.
      Long fields ending with "..." are truncated - use getEntity to fetch full content.

      Available return fields: id, name, firstName, lastName, email, mobile, phone, status, occupation (job title — NOT "title"), companyName, companyURL (NOT "website"), address(city,state,zip), owner(id,firstName,lastName), primarySkills, secondarySkills, categories, dateAdded, dateAvailable, source, employmentPreference.
      Do NOT use as return fields: title, linkedInUrl, emails (plural), website, workHistory. Use "email" (singular) for email address.
      These are RETURN fields for the "fields" parameter, not filters — "occupation" for example can be requested but not searched on.`,
  },
  searchJobOrders: {
    input: SearchJobOrdersArgs,
    output: SearchResult,
    description: `Search for Job Orders (jobs/requisitions) in Bullhorn. isDeleted:false is automatically added.
      To find jobs MATCHING a candidate or role, prefer searchJobProfiles (semantic match). Use this tool for lookups by title/status/company/owner. To verify searchJobProfiles results, pass all shortlisted ids in ONE call via the ids parameter.

      Leave "status" unset and the search skips the statuses this tenant marked finished — do that for "open jobs", "jobs we are working", and job matching. Set "status" only to search exact statuses, including closed/filled ones. Never guess a status string to mean "open"; every tenant names them differently. A lookup by "ids" returns every requested job regardless of status, so read each job's status yourself. Read "note" on the result before you describe the jobs: it says which statuses were actually searched.

      "My jobs" is ambiguous and the right filter depends on the user's role: a recruiter working reqs means assignedUserIds, an account manager who owns the client means ownerIds. They are independent fields. If you cannot tell which the user means, run both and label which list is which rather than guessing.

      Commonly used return fields: id, title, status, clientCorporation(id,name), clientContact(id,name,email,mobile), dateAdded, startDate, dateEnd, owner(id,firstName,lastName), assignedUsers(id,firstName,lastName), employmentType, numOpenings, salary, publicDescription. Any other field lookupFields lists for JobOrder is valid too.
      Pay, bill rate, and gross weekly are usually tenant custom fields (customText*, customFloat*) and "salary" is often 0 — call lookupFields on JobOrder, find the field by label, and pass its name in "fields" before saying pay is not listed.
      Field names that do NOT exist: duration, billRate, payRateLow, payUnit, openings, numOpeningsAvailable, dateBegin. The opening count is "numOpenings"; job start/end are "startDate"/"dateEnd" (NOT dateBegin).`,
  },
  searchCompanies: {
    input: SearchCompaniesArgs,
    output: SearchResult,
    description: `Search for Companies (ClientCorporation) in Bullhorn.

      Common fields: id, name, status, companyURL (NOT "website"), companyDescription (NOT "description"), address(city,state,zip,address1), phone, billingPhone, fax, owners(id,firstName,lastName) (plural, NOT "owner"), dateAdded.
      Do NOT use: website, description, email, owner (singular) — these fields do not exist. The address sub-field for street is "address1" (NOT "street") — e.g. address(city,state,zip,address1).`,
  },
  searchContacts: {
    input: SearchContactsArgs,
    output: SearchResult,
    description: `Search for Client Contacts in Bullhorn. isDeleted:false is automatically added.

      Common fields: id, name, firstName, lastName, email, phone, mobile, occupation (job title — NOT "title"), clientCorporation(id,name), status, owner(id,firstName,lastName), dateAdded.
      Do NOT use: title, linkedInUrl — these fields do not exist. The contact's job title is stored in "occupation".`,
  },
  searchLeads: {
    input: SearchLeadsArgs,
    output: SearchResult,
    description: `Search for Leads (prospects not yet converted to contacts) in Bullhorn. isDeleted:false is automatically added.

      Common fields: id, name, firstName, lastName, email, phone, mobile, occupation (job title, NOT "title"), companyName, clientCorporation(id,name), status, leadSource, owner(id,firstName,lastName), dateAdded.
      "companyName" is free text; "clientCorporation" is only set once the lead is linked to a company record.`,
  },
  searchOpportunities: {
    input: SearchOpportunitiesArgs,
    output: SearchResult,
    description: `Search for Opportunities (sales pipeline records, a potential job before it becomes a job order) in Bullhorn. isDeleted:false is automatically added. Set isOpen to true for the active pipeline.

      Common fields: id, title, clientCorporation(id,name), clientContact(id,name,email), status, isOpen, owner(id,firstName,lastName), dateAdded, expectedCloseDate, numOpenings, dealValue, winProbabilityPercent.
      Do NOT use: name. The opportunity's name is "title". Fee and pay fields ("expectedFee", "markUpPercentage", "salary", "benefits") are restricted and commonly error.`,
  },
  searchPlacements: {
    input: SearchPlacementsArgs,
    output: SearchResult,
    description: `Search for Placements in Bullhorn. Use this to find candidates working at a company. Supports filtering by candidate location via candidateState.

      ${QUERY_API_PAGING_GUIDANCE}

      "Placements made/booked/written in <range>" means the date the placement record was created: filter on dateAddedAfter/dateAddedBefore, not dateBegin* (assignment start).

      Available return fields: id, candidate(id,name,email,mobile,phone,status), clientCorporation(id,name), clientContact(id,name,email,mobile), jobOrder(id,title), owner(id,firstName,lastName), dateBegin, dateEnd, durationWeeks, status, employmentType, payRate, salary, salaryUnit, clientBillRate, billingProfile.
      Do NOT use as return fields: billRate, payUnit, user, placementUser. The owning user is "owner".`,
  },
  searchSendouts: {
    input: SearchSendoutsArgs,
    output: SearchResult,
    description: `Search for Sendouts in Bullhorn. Supports filtering by candidate location.

      ${QUERY_API_PAGING_GUIDANCE}

      Available fields: id, candidate(id,name,email,mobile,phone), clientCorporation(id,name), clientContact(id,name,email), jobOrder(id,title), dateAdded, user(id,firstName,lastName).

      Note: Sendout does NOT have a 'comments' field - use searchNotes if you need notes/comments about candidates.`,
  },
  searchJobSubmissions: {
    input: SearchJobSubmissionsArgs,
    output: SearchResult,
    description: `Search for Job Submissions (candidates submitted to jobs) in Bullhorn.

      ${QUERY_API_PAGING_GUIDANCE}

      Available return fields: id, candidate(id,name,email,mobile,phone,status), jobOrder(id,title,clientCorporation), status, dateAdded, dateLastModified, source, sendingUser(id,firstName,lastName), owners(id,firstName,lastName).
      Do NOT use as return fields: clientCorporation, user, owner (singular). The owner list is "owners" (plural) and the user who submitted is "sendingUser". Reach the company through the job order: jobOrder(id,title,clientCorporation(id,name)). Never put two parenthesized sub-objects inside one parent - jobOrder(id,title,clientCorporation(id,name),address(city)) fails to parse.`,
  },
  searchTasks: {
    input: SearchTasksArgs,
    output: SearchResult,
    description: `Search for Tasks (to-dos, calls, and reminders assigned to users) in Bullhorn. Omit isCompleted to get open and completed tasks together.

      ${QUERY_API_PAGING_GUIDANCE}

      Available return fields: id, subject, description, isCompleted, dateBegin, dateEnd, dateCompleted, type, priority, owner(id,firstName,lastName), candidate(id,name), clientContact(id,name), jobOrder(id,title), opportunity(id,title), lead(id,name), isPrivate, dateAdded.
      The assignee is "owner" (NOT "assignee" or "assignedTo"), the title is "subject", and the body is "description".`,
  },
  searchTearsheets: {
    input: SearchTearsheetsArgs,
    output: SearchResult,
    description: `Search for Tearsheets (saved lists of candidates, contacts, and jobs) in Bullhorn. Name matches the whole value exactly; omit it to list by owner or date instead.

      ${QUERY_API_PAGING_GUIDANCE}

      Available return fields: id, name, description, isPrivate, owner(id,firstName,lastName), dateAdded, dateLastModified. For a tearsheet's members, call getEntity on it with candidates(id,name), clientContacts(id,name), or jobOrders(id,title).`,
  },
  searchEmails: {
    input: SearchEmailsArgs,
    output: SearchResult,
    description: `Search for Emails (UserMessage) in Bullhorn. Requires entityId and entityType to scope the search. This is the only source of inbound email; notes hold outbound copies only.
      An email is inbound when a candidate or client contact sent it and outbound when a CorporateUser (recruiter) sent it; sender and externalFrom show which, and an auto-reply is neither. Say which direction each email went.
      subject is the only searchable text. Bodies ("comments") are not searchable, so to find emails about a topic, search subject with broad terms, or request "comments" in fields and read them.
      Use getEntity with entity "UserMessage" and the email id to fetch the full sanitized email body (the body is the "comments" field — NOT "body").

      Common fields: id, dateAdded, subject, comments, externalFrom, externalTo, sender(id,name,email), toRecipients(id,name,email), smtpReceiveDate, isPrivate.
      The ONLY valid sort field is "smtpReceiveDate". Do NOT sort by "dateAdded" or "smtpSendDate" — neither works. Example: sort: "-smtpReceiveDate" for newest first.`,
  },
  searchCandidateProfiles: {
    input: SearchCandidateProfilesArgs,
    output: SearchResult,
    description: `Semantic search over structured candidate profiles extracted from candidate activity history.
      PREFERRED tool for any match-finding request — "find candidates for this job", "who fits this role", "find candidates like X". Matches by MEANING, not keywords — e.g. specialty "ICU" also finds "Critical Care" and "CVICU" candidates. Use searchCandidates only for lookups by name/status/owner/metadata.

      For "find candidates for job X" with a known JobOrder ID: pass profileQuery { queryBy: "similarJob", similarToJobId: X } — it uses the job's extracted profile directly and auto-applies the job's required state licenses as the statesLicensed filter. Only distill requirements into idealProfile manually (via getEntity on the JobOrder) if the job has no profile embedding yet.
      Otherwise describe the IDEAL CANDIDATE via profileQuery { queryBy: "idealProfile", ... } — do not paste a whole job description. Healthcare roles: licenseType + specialty. Professional roles: discipline + industry (+ roleType). Both: skills + certifications.
      For "find candidates like candidate X": pass profileQuery { queryBy: "similarCandidate", similarToCandidateId: X } (X is excluded from results automatically).

      IMPORTANT — verify before presenting: each result reflects that candidate's profile as of profileCreatedAt, which may be stale. Cross-check the whole shortlist in ONE searchCandidates call (ids: [all shortlisted ids], with a matching limit) and drop obvious mismatches (wrong specialty/discipline, inactive or do-not-contact status, unavailable) before presenting matches to the user. Only getEntity an individual candidate when you need to dig deeper than the standard fields.
      Coverage caveat: only candidates with a generated profile are searchable — a subset of all candidates, so absence here does not mean absence in Bullhorn.

      ${PROFILE_SEARCH_RANK_GUIDANCE}`,
  },
  searchJobProfiles: {
    input: SearchJobProfilesArgs,
    output: SearchResult,
    description: `Semantic search over structured profiles of OPEN jobs, extracted from each JobOrder's record.
      PREFERRED tool for job match-finding — "find jobs for this candidate", "what openings fit X", "jobs like this one". Matches by MEANING, not keywords. Only open, non-deleted jobs are returned.

      For "find jobs for candidate X": pass profileQuery { queryBy: "similarCandidate", similarToCandidateId: X } — it uses the candidate's stored profile directly and filters job locations to the candidate's licensed + preferred states (override with states). Only describe the ideal job manually via idealProfile (from getEntity on the Candidate) if the candidate has no profile embedding yet.
      For "find jobs like job Y": pass profileQuery { queryBy: "similarJob", similarToJobId: Y } (Y is excluded from results automatically).
      Otherwise describe the IDEAL JOB via profileQuery { queryBy: "idealProfile", ... }. Healthcare: licenseType + specialty. Professional: discipline + industry (+ roleType). Both: skills + certifications.

      IMPORTANT — verify before presenting: each result reflects the job's profile as of profileCreatedAt. Cross-check the whole shortlist in ONE searchJobOrders call (ids: [all shortlisted ids], with a matching limit) and drop mismatches (closed/on-hold status, wrong specialty) before presenting.
      Coverage caveat: only jobs with a generated profile are searchable — absence here does not mean absence in Bullhorn.

      ${PROFILE_SEARCH_RANK_GUIDANCE}`,
  },
  getEntity: {
    input: GetEntityArgs,
    output: z.looseObject({ id: z.number() }).or(z.null()),
    description: `Get a single Bullhorn entity by its ID. Use when you have an ID and need details. Omit fields to use smart defaults.

      Field names vary per entity — common mistakes that will cause errors:
      - Candidate: job title is "occupation" (NOT "title"). Company website is "companyURL" (NOT "website"). Email is "email" (NOT "emails"). No "linkedInUrl" or "workHistory" fields — use "description" for work history text.
        Example: fields: "id,name,email,phone,mobile,occupation,companyName,companyURL,address(city,state,zip),owner(id,firstName,lastName),primarySkills,description,source"
      - ClientCorporation: website is "companyURL" (NOT "website"). Description is "companyDescription" (NOT "description"). Owner is "owners" plural (NOT "owner"). Street address sub-field is "address1" (NOT "street") — e.g. address(city,state,zip,address1). No "email" field — look up contacts instead.
        Example: fields: "id,name,companyURL,companyDescription,address(city,state,zip,address1),phone,owners(id,firstName,lastName),status,dateAdded"
      - ClientContact: job title is "occupation" (NOT "title"). No "linkedInUrl" field.
        Example: fields: "id,name,firstName,lastName,email,phone,mobile,occupation,clientCorporation(id,name),owner(id,firstName,lastName),dateAdded"
      - Placement: billing fields are often restricted — "billRate" commonly errors; use "payRate" and "salary" instead.
        Example: fields: "id,status,candidate(id,name),jobOrder(id,title),clientCorporation(id,name),clientContact(id,name),dateBegin,dateEnd,employmentType,payRate,salary,owner(id,firstName,lastName)"
      - Note: author is "commentingPerson(id,firstName,lastName)" (NOT "author", "recruiter", or "person").
      - UserMessage (email): the email body is "comments" (NOT "body" or "bodyText"). Sender is "sender(id,name,email)", recipients are "toRecipients(id,name,email)".
      - Lead: job title is "occupation" (NOT "title"). Where the lead came from is "leadSource". The company is the "companyName" text, plus "clientCorporation(id,name)" once it is linked to a company record.
        Example: fields: "id,name,email,phone,mobile,occupation,companyName,clientCorporation(id,name),status,leadSource,owner(id,firstName,lastName),dateAdded"
      - Task: the title is "subject", the body is "description", the assignee is "owner(id,firstName,lastName)" (NOT "assignee" or "assignedTo"). Linked records are "candidate(id,name)", "clientContact(id,name)", "jobOrder(id,title)".
        Example: fields: "id,subject,description,isCompleted,dateBegin,dateEnd,priority,owner(id,firstName,lastName),candidate(id,name)"
      - Opportunity: the name is "title" (NOT "name"). Fee and pay fields are restricted: "expectedFee", "markUpPercentage", "salary", and "benefits" commonly error; use "dealValue" and "winProbabilityPercent" instead.
        Example: fields: "id,title,clientCorporation(id,name),clientContact(id,name),status,isOpen,owner(id,firstName,lastName),dateAdded,expectedCloseDate,dealValue"
      - Tearsheet (a saved list of records): the members are the to-many fields "candidates(id,name)", "clientContacts(id,name)", and "jobOrders(id,title)". Each comes back as { total, data } holding only a first page of members, and there is no way to page further here.
      For custom fields (customText1, etc.), call lookupFields first to discover labels.`,
  },
  resolveCompany: {
    input: ResolveCompanyArgs,
    output: z.array(BullhornApiEntities.ClientCorporation.pick({ id: true, name: true })),
    description:
      'Look up a company by NAME to get its Bullhorn ClientCorporation ID. If 0 results, try progressively shorter name versions.',
  },
  resolveRecruiter: {
    input: ResolveRecruiterArgs,
    output: z.array(
      BullhornApiEntities.CorporateUser.pick({
        id: true,
        name: true,
        firstName: true,
        lastName: true,
        email: true,
      })
    ),
    description:
      'Look up a recruiter/user by name or email to get their Bullhorn CorporateUser ID. Use when you need to resolve a recruiter reference to an ID.',
  },
  generateFile: {
    input: GenerateFileArgs,
    output: FileResult,
    // Single source with the narrowed path; the docx-Markdown hint lives on the content field description.
    description: generateFileDescription(GeneratedFileFormat.options),
  },
  lookupFields: {
    input: LookupFieldsArgs,
    output: z.object({
      entityType: z.string(),
      fields: FieldInfo.array(),
      unknownFields: z.string().array().optional(),
    }),
    description:
      'Get available fields for a Bullhorn entity type, with labels and picklist values. Call it whenever the user asks about something the standard fields in a search tool description do not cover (pay rates, gross weekly, secondary owner, credentialing status, and similar), then pass the matching field name to the search tool in its "fields" param to read it or in fieldFilters to filter by it. Tenants store these in custom fields (customText1, etc.) whose label is the only way to find them. A long picklist shows its first 10 values; call lookupFields again with fields: ["name"] for the full list. Names that match nothing come back in unknownFields.',
  },
  renderChart: {
    input: RenderChartToolArgs,
    output: RenderChartArgs,
    description: `Render a chart in the chat when numeric data is clearer visually — comparisons, breakdowns, trends over time, or distributions. Call this INSTEAD of describing the chart in text. For raw tabular data that isn't worth visualizing, use a plain markdown table instead of this tool. Pass the spec as the 'chart' argument:
      - chart.chartType: 'bar' | 'line' | 'area' (time/ordered series) | 'pie' (parts of a whole).
      - chart.data: the rows. Every row is { "name": <category label, a string>, ...one number per series key }, e.g. [{ "name": "Active", "count": 42 }]. Max 100 rows.
      - chart.series: one entry per numeric measure to plot; key must match a numeric field in every data row, label is its display name. Max 6.
      A pie is stricter: exactly one series keyed 'value', and rows of { "name": "Active", "value": 42 }.
      Only chart data you actually have. Still add a one-line takeaway in text alongside the chart.`,
  },
  updateMemory: {
    input: UpdateMemoryArgs,
    output: UpdateMemoryResult,
    description: `Save, revise, or remove a durable fact about the CURRENT USER (the recruiter you are chatting with). Saved facts are shown to you under "What you remember about this user" in future chats.
      Save only durable facts about how this user works: their role, desk/market/specialty, formatting preferences, and standing instructions (e.g. "always present results as tables"). Save when the user states a preference or explicitly asks you to remember something; briefly confirm what you saved.
      Do NOT save: one-off conversation details, anything about specific candidates/contacts/clients (names, statuses, personal data), or anything you can look up in Bullhorn.
      When a new fact overlaps an existing entry, use 'update' (or 'delete') on that entry instead of adding a near-duplicate. Memory holds at most ${USER_MEMORY_MAX_ENTRIES} entries — when full, consolidate or delete before adding. When the user asks you to forget something, delete the matching entry.`,
  },
  submitCandidateToShortlist: {
    input: SubmitCandidateToShortlistArgs,
    output: SubmitCandidateToShortlistResult,
    description: `Shortlist a candidate for a job: creates an internal submission record in the ATS, attributed to the current user. This writes to the ATS.
      ONLY call this when the user explicitly asked, in the current conversation, to shortlist or submit THIS candidate to THIS job. Never call it because a candidate looks like a good match, and never as a follow-up step you decided on yourself — if the user has not asked, suggest it in text instead.
      Name the candidate and the job in the same turn you call this, so the user can see exactly what was shortlisted.
      One candidate and one job per call. For several, make one call per pairing in the same response, and name only that pairing in each displayDescription.
      If the candidate is already on this job, that existing submission is reported back and nothing is created — never call this again to retry a pairing that already exists.
      Write displayDescription as a standalone statement of the action (e.g. "Shortlist Jane Smith for RN - Nights (job 4821)") — it doubles as the text of the user's confirmation prompt, so present tense, never past tense implying it already happened.`,
    confirmation: {
      required: true,
      prompt: 'Allow Ask Toro to add to the shortlist?',
      label: 'Shortlist candidates to jobs',
    },
  },
} as const satisfies Record<ToolName, ToolDefinition>;

export type ToolInput<T extends ToolName> = z.infer<(typeof TOOL_DEFINITIONS)[T]['input']>;
export type ToolOutput<T extends ToolName> = z.infer<(typeof TOOL_DEFINITIONS)[T]['output']>;

const hasConfirmation = <T extends ToolDefinition>(
  definition: T
): definition is T & { confirmation: { required: true } } =>
  'confirmation' in definition && definition.confirmation?.required === true;

export const CONFIRMATION_TOOL_COPY = R.pipe(
  TOOL_DEFINITIONS,
  R.pickBy(hasConfirmation),
  R.mapValues(R.prop('confirmation'))
);

export const CONFIRMATION_REQUIRED_TOOLS = R.keys(CONFIRMATION_TOOL_COPY);
export type ConfirmationRequiredToolName = keyof typeof CONFIRMATION_TOOL_COPY;
export const isConfirmationRequiredTool = R.isIncludedIn(CONFIRMATION_REQUIRED_TOOLS);

//endregion
